import { createReducer, on } from '@ngrx/store';

import { RequestStatus } from '@patient-ui/shared/enums';
import { OrbiHistoryConversation } from '@patient-ui/shared/models';

import { OrbiHistoryActions } from './orbi-history.actions';
import * as AuthActions from '../auth/auth.actions';
import { OrbiError } from '../orbi/orbi-error';

export const orbiHistoryFeatureKey = 'orbiHistory';

/**
 * Page size for the history list (skip/take): 10 conversations loaded initially
 * and 10 more per lazy-scroll page (PCSA-13312).
 */
export const ORBI_HISTORY_PAGE_SIZE = 10;

export interface OrbiHistoryState {
  /** Loaded conversations (accumulated across pages, newest backend order). */
  conversations: OrbiHistoryConversation[];
  /** Load/paginate request status. */
  loadStatus: RequestStatus;
  /**
   * True once a page returns fewer than `ORBI_HISTORY_PAGE_SIZE` items — the
   * backend exposes no total/hasMore, so end-of-list is inferred client-side
   * (mirrors mobile). Drives whether "load more" is offered.
   */
  reachedEnd: boolean;
  error: OrbiError | null;
}

export const orbiHistoryInitialState: OrbiHistoryState = {
  conversations: [],
  loadStatus: RequestStatus.NotSent,
  reachedEnd: false,
  error: null,
};

export const orbiHistoryReducer = createReducer(
  orbiHistoryInitialState,
  on(
    OrbiHistoryActions.loadOrbiHistory,
    OrbiHistoryActions.loadMoreOrbiHistory,
    (state) => ({
      ...state,
      loadStatus: RequestStatus.Pending,
      error: null,
    })
  ),
  on(
    OrbiHistoryActions.loadOrbiHistorySuccess,
    (state, { conversations, reset, reachedEnd }) => ({
      ...state,
      loadStatus: RequestStatus.Success,
      // First-page load replaces; a page append concatenates.
      conversations: reset
        ? conversations
        : [...state.conversations, ...conversations],
      reachedEnd,
      error: null,
    })
  ),
  on(OrbiHistoryActions.loadOrbiHistoryFailure, (state, { error }) => ({
    ...state,
    loadStatus: RequestStatus.Failure,
    error,
  })),
  // Optimistic rename: patch the title in place on success.
  on(
    OrbiHistoryActions.renameOrbiConversationSuccess,
    (state, { sessionId, title }) => ({
      ...state,
      conversations: state.conversations.map((c) =>
        c.metadata.sessionId === sessionId
          ? { ...c, metadata: { ...c.metadata, title } }
          : c
      ),
    })
  ),
  // Remove the deleted conversation from the list on success.
  on(
    OrbiHistoryActions.deleteOrbiConversationSuccess,
    (state, { sessionId }) => ({
      ...state,
      conversations: state.conversations.filter(
        (c) => c.metadata.sessionId !== sessionId
      ),
    })
  ),
  // Clear history on logout (never leak one user's conversations to the next).
  on(AuthActions.logOutSuccess, () => ({ ...orbiHistoryInitialState }))
);
